import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  InfoBox,
  Input,
  RefreshButton,
  Stack,
  Switch,
  Table,
  Text,
  VStack,
  notify,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import {
  MdAdd,
  MdBusiness,
  MdClear,
  MdSearch,
  MdVisibility,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import {
  CustomerCompany,
  customerCompanyService,
} from "services/customerCompanyService";
import { getThemeColor } from "theme/colors";

const PAGE_SIZE = 20;

const CustomerCompanies = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<CustomerCompany[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  // Server-side paging — reload on any filter or page change.
  useEffect(() => {
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, showInactive, page]);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const result = await customerCompanyService.getAll({
        search: search || undefined,
        includeInactive: showInactive,
        page,
        pageSize: PAGE_SIZE,
      });
      setCompanies(result.items);
      setTotal(result.total);
      setError("");
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("app.customerCompanies.loadFailed", {
            defaultValue: "Failed to load companies.",
          }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput);
  };
  const handleClearFilters = () => {
    setSearchInput("");
    setSearch("");
    setShowInactive(false);
    setPage(1);
  };
  const handleShowInactiveChange = (checked: boolean) => {
    setShowInactive(checked);
    setPage(1);
  };

  const hasActiveFilters = search.length > 0 || showInactive;
  // Server returns filtered + paged results.
  const filtered = companies;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return t("app.common.never", { defaultValue: "Never" });
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleToggleActive = async (
    company: CustomerCompany,
    next: boolean,
  ) => {
    try {
      await customerCompanyService.toggleActive(company.id, next);
      setCompanies((prev) =>
        prev.map((c) => (c.id === company.id ? { ...c, isActive: next } : c)),
      );
      notify({
        type: "success",
        title: next
          ? t("app.customerCompanies.activated", {
              defaultValue: "Customer company activated.",
            })
          : t("app.customerCompanies.deactivated", {
              defaultValue:
                "Company deactivated. Its customers are signed out and its scheduled workflows are skipped.",
            }),
      });
    } catch (err: any) {
      notify({
        type: "error",
        title: t("app.errors.requestFailed", {
          defaultValue: "Request failed",
        }),
        description: err.response?.data?.error,
      });
    }
  };

  if (loading) {
    return (
      <Stack gap={4} w="full" maxW="full">
        <Heading>
          {t("app.customerCompanies.title", {
            defaultValue: "Companies",
          })}
        </Heading>
        <Card.Root
          borderWidth="1px"
          borderColor={getThemeColor("card.border")}
          borderRadius="md"
        >
          <Card.Body>
            <Text textAlign="center">
              {t("app.customerCompanies.loading", {
                defaultValue: "Loading companies...",
              })}
            </Text>
          </Card.Body>
        </Card.Root>
      </Stack>
    );
  }

  return (
    <Stack gap={4} w="full" maxW="full">
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <Heading>
          {t("app.customerCompanies.title", {
            defaultValue: "Companies",
          })}
        </Heading>
        <HStack gap={3}>
          <Button
            intent="primary"
            onClick={() => navigate("/admin/customer-companies/new")}
          >
            <MdAdd />{" "}
            {t("app.customerCompanies.create", {
              defaultValue: "New Company",
            })}
          </Button>
          <RefreshButton onClick={loadCompanies} loading={loading} />
        </HStack>
      </Flex>

      {error && (
        <InfoBox variant="error">
          <Text color="red.700">{error}</Text>
        </InfoBox>
      )}

      <Card.Root
        borderWidth="1px"
        borderColor={getThemeColor("card.border")}
        borderRadius="md"
      >
        <Card.Body>
          <Stack gap={3}>
            <HStack gap={3}>
              <Input
                placeholder={t("app.customerCompanies.searchPlaceholder", {
                  defaultValue: "Search by name or Company ID",
                })}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                flex={1}
              />
              <Button onClick={handleSearch} intent="primary">
                <MdSearch /> {t("app.common.search")}
              </Button>
              {hasActiveFilters && (
                <Button intent="secondary" onClick={handleClearFilters}>
                  <MdClear /> {t("app.common.clearFilters")}
                </Button>
              )}
            </HStack>
            <HStack gap={3}>
              <Text fontSize="sm" color="var(--text-secondary-color)">
                {t("app.customerCompanies.showInactive", {
                  defaultValue: "Show inactive",
                })}
              </Text>
              <Switch
                checked={showInactive}
                onCheckedChange={(e: any) =>
                  handleShowInactiveChange(e.checked)
                }
              />
            </HStack>
          </Stack>
        </Card.Body>
      </Card.Root>

      <Card.Root
        borderWidth="1px"
        borderColor={getThemeColor("card.border")}
        borderRadius="md"
        overflow="hidden"
      >
        <Card.Body p={0}>
          {filtered.length === 0 ? (
            <VStack gap={4} py={8}>
              <MdBusiness size={64} color="gray" />
              <Text fontSize="lg">
                {hasActiveFilters
                  ? t("app.customerCompanies.noResults", {
                      defaultValue: "No matching companies.",
                    })
                  : t("app.customerCompanies.noItems", {
                      defaultValue: "No companies yet.",
                    })}
              </Text>
              <Text
                fontSize="sm"
                color="var(--text-secondary-color)"
                textAlign="center"
                maxW="500px"
              >
                {t("app.customerCompanies.noItemsDescription", {
                  defaultValue:
                    "Companies group widget end-users and own company-scoped KB, workflows, and integrations. Create one to get started, or wait for the widget to auto-create them as customers connect.",
                })}
              </Text>
              {!hasActiveFilters && (
                <Button
                  intent="primary"
                  onClick={() => navigate("/admin/customer-companies/new")}
                >
                  <MdAdd />{" "}
                  {t("app.customerCompanies.create", {
                    defaultValue: "New Company",
                  })}
                </Button>
              )}
            </VStack>
          ) : (
            <Box overflowX="auto">
              <Table.Root minW="900px">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>
                      {t("app.customerCompanies.columns.name", {
                        defaultValue: "Name",
                      })}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customerCompanies.columns.externalRef", {
                        defaultValue: "Company ID",
                      })}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customerCompanies.columns.customers", {
                        defaultValue: "Customers",
                      })}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customerCompanies.columns.admins", {
                        defaultValue: "Admins",
                      })}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customerCompanies.columns.lastActivity", {
                        defaultValue: "Last activity",
                      })}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customerCompanies.columns.active", {
                        defaultValue: "Active",
                      })}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.common.actions", { defaultValue: "Actions" })}
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filtered.map((c) => (
                    <Table.Row
                      key={c.id}
                      cursor="pointer"
                      onClick={() =>
                        navigate(`/admin/customer-companies/${c.id}`)
                      }
                    >
                      <Table.Cell>
                        <Text fontWeight="medium">{c.name}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text
                          fontSize="sm"
                          color={
                            c.externalRef
                              ? undefined
                              : "var(--text-secondary-color)"
                          }
                          fontFamily={c.externalRef ? "mono" : undefined}
                        >
                          {c.externalRef || t("app.common.notAvailable")}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>{c.customerCount}</Table.Cell>
                      <Table.Cell>{c.customerAdminCount}</Table.Cell>
                      <Table.Cell>
                        <Text fontSize="sm">
                          {formatDate(c.lastActivityAt)}
                        </Text>
                      </Table.Cell>
                      <Table.Cell onClick={(e) => e.stopPropagation()}>
                        <Switch
                          checked={c.isActive}
                          onCheckedChange={(e: any) =>
                            handleToggleActive(c, e.checked)
                          }
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          intent="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/customer-companies/${c.id}`);
                          }}
                        >
                          <MdVisibility />{" "}
                          {t("app.common.view", { defaultValue: "View" })}
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          )}
        </Card.Body>
      </Card.Root>

      {total > 0 && (
        <Flex justify="space-between" align="center" wrap="wrap" gap={2} px={2}>
          <Text fontSize="sm" color="var(--text-secondary-color)">
            {t("app.customerCompanies.pageStatus", {
              defaultValue: "Showing {{from}}–{{to}} of {{total}}",
              from: (page - 1) * PAGE_SIZE + 1,
              to: Math.min(page * PAGE_SIZE, total),
              total,
            })}
          </Text>
          <HStack gap={2}>
            <Button
              intent="ghost"
              disabled={page === 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {t("app.common.previous", { defaultValue: "Previous" })}
            </Button>
            <Text fontSize="sm" color="var(--text-secondary-color)">
              {t("app.common.pageOf", {
                defaultValue: "Page {{page}} of {{totalPages}}",
                page,
                totalPages,
              })}
            </Text>
            <Button
              intent="ghost"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              {t("app.common.next", { defaultValue: "Next" })}
            </Button>
          </HStack>
        </Flex>
      )}
    </Stack>
  );
};

export default CustomerCompanies;
